<template>
  <div id="app">
    <header>
      <div class="brand">
        <img src="/aha_round.png" alt="AhaPay" class="logo" />
        <div>
          <h1>AhaPay</h1>
          <p class="subtitle">Accept payments on your sub-account.</p>
        </div>
      </div>
    </header>

    <section v-if="loading" class="card">
      <p>Loading your location…</p>
    </section>

    <section v-else-if="loadError" class="card error">
      <p>Couldn't load context: {{ loadError }}</p>
      <p>Open this app inside your CRM — it uses single sign-on to identify your sub-account.</p>
    </section>

    <section v-else class="card">
      <div class="location">
        <strong>Sub-account:</strong>
        <code>{{ locationId }}</code>
      </div>

      <!-- Connected status banner -->
      <div v-if="status?.connected && status?.provider === 'stripe' && status.approved === false" class="status rejected">
        Application rejected: {{ status.rejectedReason || 'Your application was not approved.' }}
      </div>
      <div v-else-if="status?.connected && status?.provider === 'stripe' && status.approved == null && status.stripeAccountId" class="status pending-review">
        Your application is pending review. We'll activate your account within 24–48 hours.
      </div>
      <div v-else-if="status?.connected && status?.provider === 'stripe'" class="status connected">
        ✓ Connected via Stripe
        <template v-if="status.stripeOnboardingComplete"> — accepting payments</template>
        <template v-else> — setup in progress</template>
      </div>
      <div v-else-if="status?.connected" class="status connected">
        ✓ Connected to Whop company <code>{{ status.whopCompanyId }}</code>
        (Whop product <code>{{ status.whopProductId }}</code>)
      </div>

      <!-- Provider selector (only when not yet connected, or switching) -->
      <div v-if="!status?.connected || switching" class="provider-select">
        <p><strong>Choose your payment processor:</strong></p>
        <div class="provider-options">
          <button
            :class="['provider-btn', { active: selectedProvider === 'stripe' }]"
            @click="selectedProvider = 'stripe'"
            type="button"
          >
            Stripe
          </button>
          <button
            :class="['provider-btn', { active: selectedProvider === 'whop' }]"
            @click="selectedProvider = 'whop'"
            type="button"
          >
            Whop
          </button>
        </div>
      </div>

      <!-- Switch provider link when already connected -->
      <div v-if="status?.connected && !switching" class="switch-link">
        <a href="#" @click.prevent="switching = true">Switch payment processor</a>
      </div>

      <!-- ═══ Stripe: connected & active ═══ -->
      <div v-if="showStripe && status?.stripeAccountId && !switching" class="stripe-section">
        <!-- Requirements warning -->
        <div v-if="hasRequirements" class="requirements-banner">
          <div v-if="hasPastDue" class="req-urgent">
            <strong>Action required:</strong> Stripe needs additional information to keep payments enabled.
          </div>
          <div v-else-if="hasCurrentlyDue" class="req-warning">
            <strong>Information needed:</strong> Stripe requires more details soon.
          </div>
          <div v-else-if="hasEventuallyDue" class="req-info">
            <strong>Heads up:</strong> Stripe will need more information eventually.
          </div>

          <button @click="showRequirementsForm = !showRequirementsForm" class="req-toggle" type="button">
            {{ showRequirementsForm ? 'Hide details' : 'View &amp; submit details' }}
          </button>

          <!-- Progressive KYC form -->
          <div v-if="showRequirementsForm" class="req-form">
            <p class="req-list-label">Fields requested:</p>
            <ul class="req-list">
              <li v-for="field in allRequiredFields" :key="field">
                <code>{{ friendlyFieldName(field) }}</code>
                <span v-if="isPastDue(field)" class="req-badge past-due">overdue</span>
                <span v-else-if="isCurrentlyDue(field)" class="req-badge currently-due">due now</span>
                <span v-else class="req-badge eventually-due">due later</span>
              </li>
            </ul>

            <!-- Verification document -->
            <div v-if="needsVerificationDocument" class="form-group">
              <h4>Identity verification</h4>
              <p class="doc-notice">Stripe requires an identity document (passport, national ID, or driver's license).</p>
              <button @click="openVerificationLink" :disabled="verifyLinkLoading" class="stripe-btn verify-btn" type="button">
                {{ verifyLinkLoading ? 'Loading…' : 'Upload identity document' }}
              </button>
              <p v-if="verifyLinkError" class="error">{{ verifyLinkError }}</p>
            </div>

            <!-- Individual fields -->
            <div v-if="needsIndividualFields" class="form-group">
              <h4>Personal information</h4>
              <div v-if="needsNameFields" class="form-row">
                <label>
                  First name
                  <input v-model="kycForm.firstName" type="text" autocomplete="given-name" />
                </label>
                <label>
                  Last name
                  <input v-model="kycForm.lastName" type="text" autocomplete="family-name" />
                </label>
              </div>
              <label v-if="needsField('individual.email')">
                Email
                <input v-model="kycForm.email" type="email" autocomplete="email" />
              </label>
              <label v-if="needsField('individual.phone')">
                Phone
                <input v-model="kycForm.phone" type="tel" autocomplete="tel" />
              </label>
              <div v-if="needsDob" class="form-row dob-row">
                <label>
                  Month
                  <input v-model.number="kycForm.dobMonth" type="number" min="1" max="12" placeholder="MM" />
                </label>
                <label>
                  Day
                  <input v-model.number="kycForm.dobDay" type="number" min="1" max="31" placeholder="DD" />
                </label>
                <label>
                  Year
                  <input v-model.number="kycForm.dobYear" type="number" min="1900" max="2010" placeholder="YYYY" />
                </label>
              </div>
              <label v-if="needsField('individual.id_number')">
                SSN / Tax ID
                <input v-model="kycForm.idNumber" type="password" autocomplete="off" placeholder="Full SSN or government ID" />
              </label>
              <label v-if="needsField('individual.ssn_last_4')">
                SSN last 4
                <input v-model="kycForm.ssnLast4" type="text" maxlength="4" autocomplete="off" placeholder="1234" />
              </label>
            </div>

            <!-- Address fields -->
            <div v-if="needsAddressFields" class="form-group">
              <h4>Address</h4>
              <label>
                Street address
                <input v-model="kycForm.addressLine1" type="text" autocomplete="address-line1" />
              </label>
              <label>
                City
                <input v-model="kycForm.addressCity" type="text" autocomplete="address-level2" />
              </label>
              <div class="form-row">
                <label>
                  State
                  <input v-model="kycForm.addressState" type="text" autocomplete="address-level1" />
                </label>
                <label>
                  Postal code
                  <input v-model="kycForm.addressPostal" type="text" autocomplete="postal-code" />
                </label>
              </div>
            </div>

            <!-- External account (bank) -->
            <div v-if="needsBankAccount" class="form-group">
              <h4>Bank account for payouts</h4>
              <label>
                Routing number
                <input v-model="kycForm.routingNumber" type="text" autocomplete="off" placeholder="110000000" />
              </label>
              <label>
                Account number
                <input v-model="kycForm.accountNumber" type="text" autocomplete="off" placeholder="000123456789" />
              </label>
            </div>

            <button v-if="needsIndividualFields || needsAddressFields || needsBankAccount" @click="submitKyc" :disabled="kycSubmitting" class="stripe-btn" type="button">
              {{ kycSubmitting ? 'Submitting…' : 'Submit information' }}
            </button>
            <p v-if="kycError" class="error">{{ kycError }}</p>
            <p v-if="kycOk" class="ok">Information submitted. Requirements updated.</p>
          </div>
        </div>

        <!-- All clear -->
        <div v-if="!hasRequirements" class="stripe-ok">
          <p>Your Stripe account is fully set up and accepting payments.</p>
        </div>
        <div class="stripe-footer">
          <p class="stripe-account-id">Account: <code>{{ status.stripeAccountId }}</code></p>
          <a href="#" @click.prevent="confirmDisconnect" class="disconnect-link">
            {{ disconnecting ? 'Disconnecting…' : 'Disconnect Stripe' }}
          </a>
        </div>
        <p v-if="disconnectError" class="error">{{ disconnectError }}</p>
      </div>

      <!-- ═══ Stripe: needs initial setup (or re-activating via switch) ═══ -->
      <!-- Also show while `switching`: a tenant that previously used Stripe keeps
           a stale stripeAccountId after moving to Whop, which would otherwise
           hide both this form (account exists) and the active section (!switching),
           leaving nothing on screen. Submitting re-runs onboard, which reuses the
           existing account and re-registers the GHL provider as Stripe. -->
      <div v-if="showStripe && (!status?.stripeAccountId || switching)" class="stripe-section">
        <form @submit.prevent="connectStripe" class="form">
          <label>
            Country
            <select v-model="stripeCountry" required>
              <option value="" disabled>Select country…</option>
              <option value="US">United States</option>
              <option value="GB">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              <option value="PH">Philippines</option>
              <option value="SG">Singapore</option>
              <option value="JP">Japan</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="NL">Netherlands</option>
              <option value="IE">Ireland</option>
              <option value="ES">Spain</option>
              <option value="IT">Italy</option>
              <option value="BR">Brazil</option>
              <option value="MX">Mexico</option>
              <option value="IN">India</option>
              <option value="MY">Malaysia</option>
              <option value="TH">Thailand</option>
              <option value="NZ">New Zealand</option>
              <option value="HK">Hong Kong</option>
            </select>
          </label>
          <label>
            Business type
            <select v-model="stripeBusinessType">
              <option value="individual">Individual / Sole proprietor</option>
              <option value="company">Company / LLC</option>
            </select>
          </label>
          <label>
            Business website or social page
            <input v-model="businessUrl" type="url" placeholder="https://your-site.com or facebook.com/page" autocomplete="url" required />
          </label>
          <label>
            What will you use AhaPay for?
            <textarea v-model="businessPurpose" rows="2" placeholder="e.g. Selling digital courses, coaching services, physical products…" required></textarea>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="stripeTosAccepted" />
            I agree to the <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noopener">Stripe Connected Account Agreement</a>
          </label>
          <button type="submit" :disabled="stripeLoading || !stripeCountry || !stripeTosAccepted || !businessUrl || !businessPurpose" class="stripe-btn">
            {{ stripeLoading ? 'Creating account…' : 'Connect with Stripe' }}
          </button>
        </form>
        <p v-if="stripeError" class="error">{{ stripeError }}</p>
      </div>

      <!-- ═══ Whop flow ═══ -->
      <div v-if="showWhop">
        <div v-if="!status?.connected || switching" class="help">
          <p><strong>Need your Whop credentials?</strong></p>
          <ol>
            <li>Go to your <a href="https://whop.com/dashboard" target="_blank" rel="noopener">Whop Dashboard</a></li>
            <li>Navigate to <strong>Settings &rarr; API Keys</strong> to create or copy your API key</li>
            <li>Your Company ID is shown on the same page (starts with <code>biz_</code>)</li>
          </ol>
          <p class="help-link"><a href="https://docs.whop.com/developer/api/getting-started" target="_blank" rel="noopener">Whop API docs &rarr;</a></p>
        </div>

        <form @submit.prevent="connect" class="form">
          <label>
            Whop API key
            <input
              v-model="whopApiKey"
              type="password"
              :required="!status?.connected"
              :placeholder="status?.whopApiKeyHint ? `Stored: ${status.whopApiKeyHint}  (leave blank to keep)` : 'Bearer token from Whop developer settings'"
              autocomplete="off"
            />
          </label>
          <label>
            Whop company ID
            <input
              v-model="whopCompanyId"
              type="text"
              :required="!status?.connected"
              placeholder="biz_xxxxxxxxxxxxxx"
              autocomplete="off"
            />
          </label>

          <button type="submit" :disabled="submitting || (!status?.connected && (!whopApiKey || !whopCompanyId))">
            {{ submitting ? "Connecting…" : status?.connected && !switching ? "Reconnect" : "Connect with Whop" }}
          </button>
        </form>
      </div>

      <p v-if="submitError" class="error">{{ submitError }}</p>
      <p v-if="submitOk" class="ok">
        Connected. AhaPay is now registered as a payment provider on this sub-account —
        enable it in <em>Payments → Integrations</em>.
      </p>
    </section>

    <footer>
      <small>Your credentials never leave your AhaPay server. They are stored per sub-account.</small>
    </footer>
  </div>
</template>

<script>
export default {
  name: "App",
  data() {
    return {
      loading: true,
      loadError: null,
      locationId: null,
      companyId: null,
      status: null,
      selectedProvider: "stripe",
      switching: false,
      // Whop
      whopApiKey: "",
      whopCompanyId: "",
      submitting: false,
      submitError: null,
      submitOk: false,
      // Stripe onboarding
      stripeLoading: false,
      stripeError: null,
      stripeCountry: "",
      stripeBusinessType: "individual",
      stripeTosAccepted: false,
      businessUrl: "",
      businessPurpose: "",
      // Verification link
      verifyLinkLoading: false,
      verifyLinkError: null,
      // Disconnect
      disconnecting: false,
      disconnectError: null,
      // Progressive KYC
      showRequirementsForm: false,
      kycSubmitting: false,
      kycError: null,
      kycOk: false,
      kycForm: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        dobMonth: null,
        dobDay: null,
        dobYear: null,
        idNumber: "",
        ssnLast4: "",
        addressLine1: "",
        addressCity: "",
        addressState: "",
        addressPostal: "",
        routingNumber: "",
        accountNumber: "",
      },
    };
  },
  computed: {
    showStripe() {
      if (this.status?.connected && !this.switching) {
        return this.status.provider === "stripe";
      }
      return this.selectedProvider === "stripe";
    },
    showWhop() {
      if (this.status?.connected && !this.switching) {
        return this.status.provider === "whop" || !this.status.provider;
      }
      return this.selectedProvider === "whop";
    },
    req() {
      return this.status?.stripeRequirements;
    },
    hasPastDue() {
      return this.req?.pastDue?.length > 0;
    },
    hasCurrentlyDue() {
      return this.req?.currentlyDue?.length > 0;
    },
    hasEventuallyDue() {
      return this.req?.eventuallyDue?.length > 0;
    },
    hasRequirements() {
      return this.hasPastDue || this.hasCurrentlyDue || this.hasEventuallyDue;
    },
    allRequiredFields() {
      const set = new Set([
        ...(this.req?.pastDue ?? []),
        ...(this.req?.currentlyDue ?? []),
        ...(this.req?.eventuallyDue ?? []),
      ]);
      return [...set];
    },
    needsNameFields() {
      return this.allRequiredFields.some(f => f === "individual.first_name" || f === "individual.last_name");
    },
    needsIndividualFields() {
      // Only show individual section for actual data fields, not verification.document
      const dataFields = ["individual.first_name", "individual.last_name", "individual.email",
        "individual.phone", "individual.dob.day", "individual.dob.month", "individual.dob.year",
        "individual.id_number", "individual.ssn_last_4"];
      return this.allRequiredFields.some(f => dataFields.includes(f));
    },
    needsDob() {
      return this.allRequiredFields.some(f => f.startsWith("individual.dob"));
    },
    needsAddressFields() {
      return this.allRequiredFields.some(f => f.includes("address") && !f.includes("verification"));
    },
    needsBankAccount() {
      return this.allRequiredFields.some(f => f.startsWith("external_account"));
    },
    needsVerificationDocument() {
      return this.allRequiredFields.some(f => f.includes("verification.document"));
    },
  },
  async mounted() {
    try {
      const user = await window.ghl.getUserData();
      this.locationId = user?.activeLocation || user?.locationId || user?.active_location;
      this.companyId = user?.companyId || user?.company_id || null;
      if (!this.locationId) throw new Error("No active location in SSO payload");
      await this.fetchStatus();
    } catch (e) {
      this.loadError = e?.message ?? String(e);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchStatus() {
      try {
        const resp = await fetch(
          `/payments/tenant?locationId=${encodeURIComponent(this.locationId)}`
        );
        if (resp.ok) {
          this.status = await resp.json();
          if (this.status?.connected) {
            this.selectedProvider = this.status.provider || "whop";
            if (this.status.whopCompanyId) {
              this.whopCompanyId = this.status.whopCompanyId;
            }
            // Pre-fill country if a prior Stripe account exists, so re-activating
            // via "switch" doesn't start from a blank form.
            if (this.status.stripeCountry && !this.stripeCountry) {
              this.stripeCountry = this.status.stripeCountry;
            }
          }
        }
      } catch {
        // non-fatal
      }
    },
    async openVerificationLink() {
      this.verifyLinkLoading = true;
      this.verifyLinkError = null;
      try {
        const resp = await fetch("/payments/stripe/verification-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locationId: this.locationId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error?.message ?? data.error ?? `HTTP ${resp.status}`);
        window.open(data.url, "_blank");
      } catch (e) {
        this.verifyLinkError = e?.message ?? String(e);
      } finally {
        this.verifyLinkLoading = false;
      }
    },
    async confirmDisconnect() {
      if (!confirm("Disconnect your Stripe account? This will delete the connected account and you'll need to set up a new one.")) return;
      this.disconnecting = true;
      this.disconnectError = null;
      try {
        const resp = await fetch("/payments/stripe/disconnect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locationId: this.locationId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error?.message ?? data.error ?? `HTTP ${resp.status}`);
        this.switching = false;
        await this.fetchStatus();
      } catch (e) {
        this.disconnectError = e?.message ?? String(e);
      } finally {
        this.disconnecting = false;
      }
    },
    async connectStripe() {
      this.stripeLoading = true;
      this.stripeError = null;
      this.submitError = null;
      this.submitOk = false;
      try {
        const resp = await fetch("/payments/stripe/onboard", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            locationId: this.locationId,
            companyId: this.companyId,
            country: this.stripeCountry,
            businessType: this.stripeBusinessType,
            tosAccepted: this.stripeTosAccepted,
            businessUrl: this.businessUrl,
            businessPurpose: this.businessPurpose,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error?.message ?? data.error ?? `HTTP ${resp.status}`);
        this.submitOk = true;
        this.switching = false;
        await this.fetchStatus();
      } catch (e) {
        this.stripeError = e?.message ?? String(e);
      } finally {
        this.stripeLoading = false;
      }
    },
    needsField(field) {
      return this.allRequiredFields.includes(field);
    },
    isPastDue(field) {
      return this.req?.pastDue?.includes(field);
    },
    isCurrentlyDue(field) {
      return this.req?.currentlyDue?.includes(field);
    },
    countryCurrency(country) {
      const map = {
        US: "usd", GB: "gbp", CA: "cad", AU: "aud", PH: "php",
        SG: "sgd", JP: "jpy", DE: "eur", FR: "eur", NL: "eur",
        IE: "eur", ES: "eur", IT: "eur", BR: "brl", MX: "mxn",
        IN: "inr", MY: "myr", TH: "thb", NZ: "nzd", HK: "hkd",
      };
      return map[country] || "usd";
    },
    friendlyFieldName(field) {
      const map = {
        "individual.first_name": "First name",
        "individual.last_name": "Last name",
        "individual.email": "Email",
        "individual.phone": "Phone",
        "individual.dob.day": "Date of birth (day)",
        "individual.dob.month": "Date of birth (month)",
        "individual.dob.year": "Date of birth (year)",
        "individual.address.line1": "Street address",
        "individual.address.city": "City",
        "individual.address.state": "State",
        "individual.address.postal_code": "Postal code",
        "individual.id_number": "SSN / Tax ID",
        "individual.ssn_last_4": "SSN last 4 digits",
        "external_account": "Bank account",
        "business_profile.url": "Business website URL",
        "business_profile.mcc": "Business category",
      };
      return map[field] || field;
    },
    async submitKyc() {
      this.kycSubmitting = true;
      this.kycError = null;
      this.kycOk = false;
      try {
        const body = { locationId: this.locationId };
        const f = this.kycForm;

        // Build individual object from filled fields
        const individual = {};
        if (f.firstName) individual.first_name = f.firstName;
        if (f.lastName) individual.last_name = f.lastName;
        if (f.email) individual.email = f.email;
        if (f.phone) individual.phone = f.phone;
        if (f.dobMonth && f.dobDay && f.dobYear) {
          individual.dob = { month: f.dobMonth, day: f.dobDay, year: f.dobYear };
        }
        if (f.idNumber) individual.id_number = f.idNumber;
        if (f.ssnLast4) individual.ssn_last_4 = f.ssnLast4;
        if (f.addressLine1 || f.addressCity || f.addressState || f.addressPostal) {
          individual.address = {};
          if (f.addressLine1) individual.address.line1 = f.addressLine1;
          if (f.addressCity) individual.address.city = f.addressCity;
          if (f.addressState) individual.address.state = f.addressState;
          if (f.addressPostal) individual.address.postal_code = f.addressPostal;
        }
        if (Object.keys(individual).length > 0) body.individual = individual;

        // External account (bank)
        if (f.routingNumber && f.accountNumber) {
          const acctCountry = this.status?.stripeCountry || this.stripeCountry || "US";
          body.externalAccount = {
            object: "bank_account",
            country: acctCountry,
            currency: this.countryCurrency(acctCountry),
            routing_number: f.routingNumber,
            account_number: f.accountNumber,
          };
        }

        const resp = await fetch("/payments/stripe/update-account", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error?.message ?? data.error ?? `HTTP ${resp.status}`);
        this.kycOk = true;
        await this.fetchStatus();
        // Clear form fields that were submitted
        this.kycForm = {
          firstName: "", lastName: "", email: "", phone: "",
          dobMonth: null, dobDay: null, dobYear: null,
          idNumber: "", ssnLast4: "",
          addressLine1: "", addressCity: "", addressState: "", addressPostal: "",
          routingNumber: "", accountNumber: "",
        };
      } catch (e) {
        this.kycError = e?.message ?? String(e);
      } finally {
        this.kycSubmitting = false;
      }
    },
    async connect() {
      this.submitting = true;
      this.submitError = null;
      this.submitOk = false;
      try {
        const resp = await fetch("/payments/connect", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            locationId: this.locationId,
            companyId: this.companyId,
            whopApiKey: this.whopApiKey,
            whopCompanyId: this.whopCompanyId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data.error?.message ?? data.error ?? `HTTP ${resp.status}`);
        this.submitOk = true;
        this.switching = false;
        this.whopApiKey = "";
        await this.fetchStatus();
      } catch (e) {
        this.submitError = e?.message ?? String(e);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style>
body { margin: 0; background: #f7f7f8; color: #1f2328; }
#app {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  max-width: 560px;
  margin: 40px auto;
  padding: 0 16px;
}
.brand { display: flex; align-items: center; gap: 14px; margin-bottom: 24px; }
.logo { width: 48px; height: 48px; border-radius: 50%; }
header h1 { margin: 0 0 4px; font-size: 24px; }
header .subtitle { margin: 0; color: #656d76; }
.card {
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}
.card.error { border-color: #cf222e; }
.location { margin-bottom: 16px; font-size: 14px; }
.location code { background: #eff1f3; padding: 2px 6px; border-radius: 4px; }
.status {
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
}
.status.connected { background: #dafbe1; color: #0a5d1e; }
.status.pending-review { background: #fff8c5; color: #6a5300; }
.status.rejected { background: #ffebe9; color: #82071e; }
.status code { background: rgba(0,0,0,.06); padding: 1px 5px; border-radius: 3px; }
.form { display: flex; flex-direction: column; gap: 16px; }
label { display: flex; flex-direction: column; gap: 6px; font-size: 14px; font-weight: 500; }
input {
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
}
textarea {
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
}
input:focus, textarea:focus { outline: none; border-color: #0969da; box-shadow: 0 0 0 3px rgba(9,105,218,.2); }
button {
  padding: 10px 16px;
  background: #1f2328;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
button[disabled] { opacity: .5; cursor: not-allowed; }
.error { color: #cf222e; margin-top: 12px; font-size: 14px; }
.ok { color: #0a5d1e; margin-top: 12px; font-size: 14px; }
.help {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  padding: 14px 18px;
  margin-bottom: 16px;
  font-size: 13px;
  line-height: 1.5;
}
.help p { margin: 0 0 8px; }
.help ol { margin: 0 0 8px; padding-left: 20px; }
.help li { margin-bottom: 4px; }
.help code { background: #eff1f3; padding: 1px 5px; border-radius: 3px; }
.help a { color: #0969da; text-decoration: none; }
.help a:hover { text-decoration: underline; }
.help-link { margin: 0; }
.provider-select { margin-bottom: 16px; }
.provider-select p { margin: 0 0 10px; font-size: 14px; }
.provider-options { display: flex; gap: 8px; }
.provider-btn {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #d0d7de;
  border-radius: 8px;
  background: #fff;
  color: #1f2328;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color .15s, background .15s;
}
.provider-btn:hover { border-color: #0969da; }
.provider-btn.active { border-color: #0969da; background: #ddf4ff; }
.switch-link { margin-bottom: 16px; font-size: 13px; }
.switch-link a { color: #0969da; text-decoration: none; }
.switch-link a:hover { text-decoration: underline; }
.stripe-section { margin-bottom: 16px; }
.stripe-section p { font-size: 14px; line-height: 1.5; margin: 0 0 12px; }
.stripe-btn {
  padding: 10px 20px;
  background: #635bff;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.stripe-btn:hover { background: #5148e6; }
.stripe-btn[disabled] { opacity: .5; cursor: not-allowed; }
.stripe-ok p { color: #0a5d1e; }
.stripe-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 12px; }
.stripe-account-id { font-size: 12px; color: #656d76; margin: 0; }
.stripe-account-id code { background: #eff1f3; padding: 1px 5px; border-radius: 3px; }
.disconnect-link { font-size: 12px; color: #cf222e; text-decoration: none; white-space: nowrap; }
.disconnect-link:hover { text-decoration: underline; }
select {
  padding: 10px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  background: #fff;
}
select:focus { outline: none; border-color: #0969da; box-shadow: 0 0 0 3px rgba(9,105,218,.2); }
.checkbox-label {
  flex-direction: row !important;
  align-items: center;
  gap: 8px !important;
  font-weight: 400 !important;
  font-size: 13px !important;
  line-height: 1.4;
}
.checkbox-label input[type="checkbox"] { width: 16px; height: 16px; margin: 0; flex-shrink: 0; }
.checkbox-label a { color: #0969da; text-decoration: none; }
.checkbox-label a:hover { text-decoration: underline; }
/* Requirements */
.requirements-banner { margin-bottom: 16px; }
.req-urgent { background: #ffebe9; border: 1px solid #cf222e; border-radius: 6px; padding: 12px; font-size: 14px; color: #82071e; margin-bottom: 8px; }
.req-warning { background: #fff8c5; border: 1px solid #d4a72c; border-radius: 6px; padding: 12px; font-size: 14px; color: #6a5300; margin-bottom: 8px; }
.req-info { background: #ddf4ff; border: 1px solid #54aeff; border-radius: 6px; padding: 12px; font-size: 14px; color: #0550ae; margin-bottom: 8px; }
.req-toggle {
  background: none;
  border: none;
  color: #0969da;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  font-weight: 500;
}
.req-toggle:hover { text-decoration: underline; }
.req-form { margin-top: 16px; }
.req-list-label { font-size: 13px; font-weight: 500; margin: 0 0 6px; }
.req-list { list-style: none; padding: 0; margin: 0 0 16px; }
.req-list li { font-size: 13px; padding: 4px 0; display: flex; align-items: center; gap: 8px; }
.req-list code { background: #eff1f3; padding: 1px 5px; border-radius: 3px; }
.req-badge { font-size: 11px; padding: 1px 6px; border-radius: 10px; font-weight: 500; }
.req-badge.past-due { background: #ffebe9; color: #82071e; }
.req-badge.currently-due { background: #fff8c5; color: #6a5300; }
.req-badge.eventually-due { background: #ddf4ff; color: #0550ae; }
.form-group { margin-bottom: 20px; }
.form-group h4 { margin: 0 0 12px; font-size: 14px; color: #1f2328; }
.doc-notice { font-size: 13px; line-height: 1.5; color: #656d76; margin: 0 0 12px; }
.verify-btn { margin-top: 0; }
.form-row { display: flex; gap: 12px; }
.form-row label { flex: 1; }
.dob-row label { flex: 0 0 auto; }
.dob-row input { width: 70px; }
footer { margin-top: 24px; text-align: center; color: #656d76; }
</style>
